'use client';

import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, XCircle, AlertCircle, Plus, Trash2, Copy } from 'lucide-react';
import { DynamicField } from './DynamicField';
import { useTheme } from '@/contexts/ThemeContext';
import { type FormThemeConfig, isThemeDark, resolveFormTheme } from './formThemes';
import type { FormField as RuntimeFormField, FormSection as RuntimeFormSection } from './formConfigurationUtils';
import type { FieldValue, FormDataValues } from './formFieldTypes';

/** Runtime field shape plus legacy conditional/onChange metadata. */
interface SectionField extends RuntimeFormField {
  width?: string;
  conditional?: {
    field: string;
    value: FieldValue;
  };
  onChange?: {
    action: string;
    targetFields: string[];
    showWhen: boolean;
    clearWhen: boolean;
  };
}

interface FormSection extends RuntimeFormSection {
  fields: SectionField[];
  instance_names?: string[]; // e.g. ["Boiler A", "Boiler B"] → headings use names instead of numbers
}

interface DynamicSectionProps {
  section: FormSection;
  formData: FormDataValues;
  expandedSections: Record<string, boolean>;
  onToggle: (sectionKey: string) => void;
  onFieldChange: (fieldKey: string, value: FieldValue) => void;
  isCompleted: boolean;
  isEmpty: boolean;
  enableImageUpload?: boolean;
  fullFormData?: FormDataValues; // Pass full form data for field dependencies
  /** Optional form-level theme override */
  formTheme?: Partial<FormThemeConfig> | null;
  /** Force single-column layout (for mobile preview simulation) */
  forceMobileLayout?: boolean;
  /** When true, all fields render as non-interactive read-only displays */
  readOnly?: boolean;
  /** Field keys the rules engine wants hidden (overridden by shownFields) */
  hiddenFields?: Set<string>;
  /** Field keys the rules engine wants force-shown (overrides hiddenFields) */
  shownFields?: Set<string>;
  /** Field keys the rules engine dynamically marks required */
  dynamicRequired?: Set<string>;
  /** Field keys the rules engine dynamically marks not-required */
  dynamicUnrequired?: Set<string>;
  /** Optional number chip shown before each field label */
  fieldNumber?: number;
  /** When true, focus the first focusable field on mount (step view) */
  autoFocus?: boolean;
}

export const DynamicSection: React.FC<DynamicSectionProps> = ({
  section,
  formData,
  expandedSections,
  onToggle,
  onFieldChange,
  isCompleted,
  isEmpty,
  enableImageUpload = false,
  fullFormData,
  formTheme: ft,
  forceMobileLayout = false,
  readOnly = false,
  hiddenFields,
  shownFields,
  dynamicRequired,
  dynamicUnrequired,
  fieldNumber,
  autoFocus = false,
}) => {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!autoFocus || !sectionRef.current) return;
    const focusable = sectionRef.current.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    );
    if (focusable) focusable.focus();
  }, [autoFocus, section.key]);

  const themeConfig = useMemo(() => resolveFormTheme(ft), [ft]);
  const hasTheme = !!ft;
  const themeIsDark = hasTheme ? isThemeDark(themeConfig) : isDark;
  const isExpanded = expandedSections[section.key] || false;
  const isCollapsible = section.styling?.behavior?.collapsible !== false;

  const resolvePositiveInt = useCallback((value: unknown, fallback: number) => {
    const normalized = Number(value);
    return Number.isFinite(normalized) && normalized > 0 ? Math.floor(normalized) : fallback;
  }, []);

  // ── Repeatable section state ──────────────────────────────────
  const isRepeatable = section.is_repeatable === true;
  const repeatMode = section.repeat_mode;
  const fixedInstances = useMemo(() => {
    if (!isRepeatable) return null;
    if (repeatMode !== 'fixed' && section.lock_instance_count !== true && section.fixed_instances == null) {
      return null;
    }

    return resolvePositiveInt(
      section.fixed_instances ?? section.min_instances ?? section.min_repeats ?? 1,
      1,
    );
  }, [isRepeatable, repeatMode, resolvePositiveInt, section.fixed_instances, section.lock_instance_count, section.min_instances, section.min_repeats]);
  // Support both naming conventions: min_instances (DB/AI) + min_repeats (builder)
  const minInstances = fixedInstances ?? resolvePositiveInt(section.min_instances ?? section.min_repeats ?? 1, 1);
  const maxInstances = useMemo(() => {
    if (fixedInstances != null) return fixedInstances;
    return Math.max(
      minInstances,
      resolvePositiveInt(section.max_instances ?? section.max_repeats ?? 20, 20),
    );
  }, [fixedInstances, minInstances, resolvePositiveInt, section.max_instances, section.max_repeats]);
  const lockInstanceCount = isRepeatable && (section.lock_instance_count === true || fixedInstances != null || repeatMode === 'fixed');
  const allowAdd = isRepeatable && !lockInstanceCount && section.allow_add !== false;
  const allowRemove = isRepeatable && !lockInstanceCount && section.allow_remove !== false;
  const allowDuplicate = isRepeatable && !lockInstanceCount && section.allow_duplicate !== false;
  const instanceLabel = section.instance_label
    || (section.repeat_label_template ? section.repeat_label_template.replace(/\{\{(?:n|index)\}\}/g, '').trim() : '')
    || section.title;

  // For repeatable sections, formData[section.key] is an array of instance records
  const sectionKey = section.key;
  // Seed for padded/newly-added instances: readonly (system-acquired, e.g.
  // work-item-linked) fields copy their value from existing instances so a
  // linked job_number/technician_name stays populated on every instance. The
  // ref survives delete-all-then-add so a linked field never re-adds empty.
  const seedCacheRef = useRef<FormDataValues>({});
  const instanceSeed = useMemo(() => {
    const seed: FormDataValues = {};
    const raw = formData[sectionKey];
    if (Array.isArray(raw)) {
      for (const f of section.fields) {
        if (!(f.readonly || f.is_readonly)) continue;
        for (const inst of raw) {
          if (inst && typeof inst === 'object' && !Array.isArray(inst)) {
            const v = (inst as FormDataValues)[f.key];
            if (v !== undefined && v !== null && v !== '') {
              seed[f.key] = v;
              break;
            }
          }
        }
      }
    }
    if (Object.keys(seed).length > 0) {
      seedCacheRef.current = seed;
      return seed;
    }
    return seedCacheRef.current;
  }, [formData, sectionKey, section.fields]);
  const instances: FormDataValues[] = useMemo(() => {
    if (!isRepeatable) return [];
    const val = formData[sectionKey];
    const normalizedInstances = Array.isArray(val) ? val.map((entry) => (
      entry && typeof entry === 'object' && !Array.isArray(entry) ? (entry as FormDataValues) : {}
    )) : [];

    if (fixedInstances != null) {
      if (normalizedInstances.length === fixedInstances) {
        return normalizedInstances;
      }

      if (normalizedInstances.length > fixedInstances) {
        return normalizedInstances.slice(0, fixedInstances);
      }

      return [
        ...normalizedInstances,
        ...Array.from({ length: fixedInstances - normalizedInstances.length }, () => ({ ...instanceSeed })),
      ];
    }

    if (normalizedInstances.length > 0) {
      if (normalizedInstances.length < minInstances) {
        return [
          ...normalizedInstances,
          ...Array.from({ length: minInstances - normalizedInstances.length }, () => ({ ...instanceSeed })),
        ];
      }
      return normalizedInstances;
    }

    // Initialize with min_instances empty objects (seeded with readonly values)
    return Array.from({ length: minInstances }, () => ({ ...instanceSeed }));
  }, [fixedInstances, formData, instanceSeed, isRepeatable, minInstances, sectionKey]);

  const canAddInstance = allowAdd && instances.length < maxInstances;
  const canRemoveInstance = allowRemove && instances.length > minInstances;
  const canDuplicateInstance = allowDuplicate && instances.length < maxInstances;

  const getInstanceHeading = useCallback((idx: number) => {
    const instanceName = (Array.isArray(section.instance_names) ? section.instance_names[idx] : undefined)?.trim() || '';

    if (section.repeat_label_template) {
      return section.repeat_label_template
        .replace(/\{\{(?:n|index)\}\}/g, String(idx + 1))
        .replace(/\{\{name\}\}/g, instanceName || String(idx + 1));
    }

    if (instanceName) {
      return `${instanceLabel} ${instanceName}`;
    }

    return `${instanceLabel} ${idx + 1}`;
  }, [instanceLabel, section.instance_names, section.repeat_label_template]);

  const handleAddInstance = useCallback(() => {
    if (!canAddInstance) return;
    onFieldChange(section.key, [...instances, { ...instanceSeed }]);
  }, [canAddInstance, instanceSeed, instances, section.key, onFieldChange]);

  const handleDuplicateInstance = useCallback((idx: number) => {
    if (!canDuplicateInstance) return;
    const copy = { ...instances[idx] };
    const updated = [...instances];
    updated.splice(idx + 1, 0, copy);
    onFieldChange(section.key, updated);
  }, [canDuplicateInstance, instances, section.key, onFieldChange]);

  const handleRemoveInstance = useCallback((idx: number) => {
    if (!canRemoveInstance) return;
    const updated = instances.filter((_, i) => i !== idx);
    onFieldChange(section.key, updated);
  }, [canRemoveInstance, instances, section.key, onFieldChange]);

  const handleInstanceFieldChange = useCallback((instanceIdx: number, fieldKey: string, value: FieldValue) => {
    const updated = instances.map((inst, i) =>
      i === instanceIdx ? { ...inst, [fieldKey]: value } : inst
    );
    onFieldChange(section.key, updated);
  }, [instances, section.key, onFieldChange]);

  // Check if a field should be visible based on conditional logic
  const shouldShowField = (field: SectionField, scopedFormData: FormDataValues = formData): boolean => {
    // Support legacy format: field.conditional = { field, value }
    if (field.conditional) {
      const { field: conditionField, value: conditionValue } = field.conditional;
      let currentValue = scopedFormData[conditionField];

      if (conditionField === 'enforce_geofence' && currentValue === undefined) {
        currentValue = scopedFormData['requires_geofence'];
      }

      if (typeof conditionValue === 'boolean') {
        return currentValue === conditionValue;
      }

      return currentValue == conditionValue;
    }

    // Support builder format: field.conditional_logic = { depends_on, show_when, operator }
    const cl = field.conditional_logic;
    if (cl && typeof cl === 'object' && Object.keys(cl).length > 0) {
      const dependsOn = cl.depends_on ?? cl.dependsOn ?? cl.field;
      if (!dependsOn || typeof dependsOn !== 'string') return true;

      const currentValue = scopedFormData[dependsOn];
      const expectedValue = cl.show_when ?? cl.equals ?? cl.value;
      const operator = typeof cl.operator === 'string' ? cl.operator : 'equals';

      switch (operator) {
        case 'is_empty':
          return currentValue === null || currentValue === undefined || currentValue === '';
        case 'is_not_empty':
          return currentValue !== null && currentValue !== undefined && currentValue !== '';
        case 'not_equals':
          if (Array.isArray(currentValue)) return !currentValue.includes(expectedValue);
          return currentValue != expectedValue;
        case 'contains':
          return String(currentValue ?? '').toLowerCase().includes(String(expectedValue ?? '').toLowerCase());
        case 'equals':
        default:
          if (expectedValue === undefined) {
            return Boolean(currentValue);
          }
          if (Array.isArray(currentValue)) {
            return currentValue.includes(expectedValue);
          }
          return currentValue == expectedValue;
      }
    }

    return true;
  };

  // Rules-engine field visibility: hide unless explicitly shown
  const isFieldHiddenByRules = (key: string): boolean =>
    !!hiddenFields && hiddenFields.has(key) && !(shownFields && shownFields.has(key));

  // Effective required flag after rules-engine require/unrequire actions.
  // dynamicRequired takes priority over dynamicUnrequired (matches the rules
  // engine's own priority logic in evaluateRules).
  const effectiveFieldRequired = (field: SectionField): boolean => {
    const baseRequired = !!(field.required || field.is_required);
    if (dynamicRequired?.has(field.key)) return true;
    if (dynamicUnrequired?.has(field.key)) return false;
    return baseRequired;
  };

  // Handle field change with onChange actions
  const handleFieldChange = (fieldKey: string, value: FieldValue) => {
    // Find the field to check for onChange actions
    const field = section.fields.find(f => f.key === fieldKey);
    
    if (field?.onChange) {
      const { action, targetFields, showWhen, clearWhen } = field.onChange;
      
      if (action === 'toggleFields') {
        // If the field value doesn't match showWhen, clear target fields
        if (value !== showWhen) {
          // Clear all target fields
          targetFields.forEach(targetField => {
            onFieldChange(targetField, null);
          });
        }
      }
    }
    
    // Always update the main field
    onFieldChange(fieldKey, value);
  };

  const handleToggle = (e: React.MouseEvent) => {
    // Don't toggle if clicking on an input field or its container
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.tagName === 'SELECT' ||
        target.closest('input') ||
        target.closest('textarea') ||
        target.closest('select') ||
        target.closest('.pac-container')) {
      return;
    }
    
    if (isCollapsible) {
      onToggle(section.key);
    }
  };

  const getStatusIcon = () => {
    // Get required fields count
    const requiredFields = section.fields.filter(field => field.required || field.is_required);
    const isRequired = section.required || section.is_required || section.validation_mode === 'all';
    
    if (isCompleted) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (isRequired && !isEmpty) {
      // Required section with some data but not complete - show amber
      return <AlertCircle className="h-5 w-5 text-amber-500" />;
    } else if (isRequired && isEmpty) {
      // Required section with no data - show red
      return <XCircle className="h-5 w-5 text-red-500" />;
    } else if (!isEmpty) {
      // Optional section with some data - show blue dot
      return <div className="h-2 w-2 rounded-full bg-blue-500"></div>;
    }
    
    return null;
  };

  const getSectionHeaderClasses = () => {
    let baseClasses = `flex items-center justify-between p-3 md:p-4 border-b transition-colors ${
      isDark 
        ? 'border-gray-600 bg-gray-800 text-white' 
        : 'border-gray-200 bg-white text-gray-900'
    }`;
    
    if (isCollapsible) {
      baseClasses += isDark 
        ? ' cursor-pointer hover:bg-gray-700' 
        : ' cursor-pointer hover:bg-gray-50';
    }
    
    if (isCompleted) {
      baseClasses += isDark ? ' bg-green-900/30' : ' bg-green-50';
    } else if (!isEmpty) {
      baseClasses += isDark ? ' bg-blue-900/30' : ' bg-blue-50';
    }

    return baseClasses;
  };

  const getSectionBodyClasses = () => {
    let baseClasses = `p-4 md:p-6 space-y-4 ${
      isDark ? 'bg-gray-800' : 'bg-white'
    }`;
    
    if (section.styling?.classes) {
      baseClasses += ` ${section.styling.classes}`;
    }

    return baseClasses;
  };

  return (
    <div
      ref={sectionRef}
      className={`rounded-lg shadow-sm border transition-colors ${
        hasTheme
          ? ''
          : themeIsDark 
            ? 'bg-gray-800 border-gray-600' 
            : 'bg-white border-gray-200'
      } ${section.styling?.containerClasses || ''}`}
      style={hasTheme ? {
        background: themeConfig.card_color,
        borderColor: themeConfig.input_border_color || `${themeConfig.accent_color}30`,
        borderRadius: themeConfig.border_radius ? `${themeConfig.border_radius}px` : undefined,
      } : undefined}
    >
      {/* Section Header */}
      <div 
        className={hasTheme ? `flex items-center justify-between p-3 md:p-4 border-b transition-colors ${
          isCollapsible ? 'cursor-pointer' : ''
        }` : getSectionHeaderClasses()} 
        onClick={handleToggle}
        style={hasTheme ? {
          cursor: isCollapsible ? 'pointer' : 'default',
          background: themeConfig.section_header_bg || themeConfig.card_color,
          borderColor: themeConfig.input_border_color || `${themeConfig.accent_color}20`,
          borderTopLeftRadius: themeConfig.border_radius ? `${themeConfig.border_radius}px` : undefined,
          borderTopRightRadius: themeConfig.border_radius ? `${themeConfig.border_radius}px` : undefined,
        } : { cursor: isCollapsible ? 'pointer' : 'default' }}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center space-x-3">
            {isCollapsible && (
              <div className="flex-shrink-0">
                {isExpanded ? (
                <ChevronDown className="h-5 w-5" style={hasTheme ? { color: themeConfig.accent_color } : undefined} />
              ) : (
                <ChevronRight className="h-5 w-5" style={hasTheme ? { color: themeConfig.accent_color } : undefined} />
              )}
              </div>
            )}
            
            <div className="flex-1">
              <h3 className={`text-base sm:text-lg font-semibold ${
                hasTheme ? '' : themeIsDark ? 'text-white' : 'text-gray-900'
              }`} style={hasTheme ? { color: themeConfig.question_color } : undefined}>
                {section.title}
                {(section.required || section.is_required) && <span className="text-red-500 ml-1">*</span>}
              </h3>
              {section.description && (
                <p className={`text-sm mt-1 ${
                  hasTheme ? '' : themeIsDark ? 'text-gray-300' : 'text-gray-600'
                }`} style={hasTheme ? { color: themeConfig.answer_color } : undefined}>{section.description}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-end ml-4">
            {getStatusIcon()}
          </div>
        </div>
      </div>

      {/* Section Body */}
      {(!isCollapsible || isExpanded) && (
        <div
          className={hasTheme ? 'p-4 md:p-6 space-y-4' : getSectionBodyClasses()}
          style={hasTheme ? { background: themeConfig.card_color } : undefined}
        >
          {isRepeatable ? (
            /* ── Repeatable Section Instances ──────────────────── */
            <div className="space-y-4">
              {instances.map((instanceData, idx) => (
                (() => {
                  const scopedInstanceFormData = {
                    ...(fullFormData || formData),
                    ...instanceData,
                  };

                  return (
                <div
                  key={`${section.key}-instance-${idx}`}
                  className={`rounded-lg border p-4 ${
                    hasTheme ? '' : isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                  }`}
                  style={hasTheme ? {
                    background: themeConfig.input_bg_color || themeConfig.card_color,
                    borderColor: themeConfig.input_border_color || `${themeConfig.accent_color}30`,
                  } : undefined}
                >
                  {/* Instance header */}
                  <div className="flex items-center justify-between mb-3">
                    <h4
                      className={`text-sm font-semibold ${hasTheme ? '' : isDark ? 'text-gray-200' : 'text-gray-700'}`}
                      style={hasTheme ? { color: themeConfig.question_color } : undefined}
                    >
                      {getInstanceHeading(idx)}
                    </h4>
                    <div className="flex items-center gap-1">
                      {!readOnly && canDuplicateInstance && (
                        <button
                          type="button"
                          onClick={() => handleDuplicateInstance(idx)}
                          className={`p-1 rounded transition-colors ${
                            isDark ? 'text-gray-400 hover:text-teal-400 hover:bg-gray-700' : 'text-gray-400 hover:text-teal-600 hover:bg-gray-200'
                          }`}
                          title="Duplicate"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!readOnly && canRemoveInstance && (
                        <button
                          type="button"
                          onClick={() => handleRemoveInstance(idx)}
                          className={`p-1 rounded transition-colors ${
                            isDark ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700' : 'text-gray-400 hover:text-red-600 hover:bg-gray-200'
                          }`}
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Instance fields */}
                  <div className={`grid gap-4 w-full ${
                    forceMobileLayout ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-2 md:gap-6'
                  }`}>
                    {(() => {
                      const visibleFields = [...section.fields]
                        .sort((a, b) => a.sort_order - b.sort_order)
                        .filter(field => shouldShowField(field, scopedInstanceFormData) && !isFieldHiddenByRules(field.key));
                      return visibleFields.map((field, fieldIndex) => {
                        const required = effectiveFieldRequired(field);
                        const effectiveFieldNumber = fieldNumber != null ? fieldNumber + fieldIndex + idx * visibleFields.length : undefined;
                        return (
                        <div
                          key={`${section.key}-${idx}-${field.key}-${fieldIndex}`}
                          className={`${field.width || 'col-span-1'} ${field.styling?.containerClasses || ''}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DynamicField
                            field={{ ...field, required, is_required: required }}
                            value={instanceData[field.key]}
                            onChange={(value) => handleInstanceFieldChange(idx, field.key, value)}
                            enableImageUpload={enableImageUpload}
                            fullFormData={scopedInstanceFormData}
                            readOnly={readOnly}
                            fieldNumber={effectiveFieldNumber}
                          />
                        </div>
                        );
                      });
                    })()}
                  </div>
                </div>
                  );
                })()
              ))}

              {/* Add instance button */}
              {!readOnly && canAddInstance && (
                <button
                  type="button"
                  onClick={handleAddInstance}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed text-sm font-medium transition-colors ${
                    hasTheme
                      ? ''
                      : isDark
                        ? 'border-gray-600 text-gray-400 hover:border-teal-500 hover:text-teal-400'
                        : 'border-gray-300 text-gray-500 hover:border-teal-500 hover:text-teal-600'
                  }`}
                  style={hasTheme ? {
                    borderColor: themeConfig.input_border_color || `${themeConfig.accent_color}40`,
                    color: themeConfig.answer_color,
                  } : undefined}
                >
                  <Plus className="w-4 h-4" />
                  Add {instanceLabel} ({instances.length}/{maxInstances})
                </button>
              )}
            </div>
          ) : (
            /* ── Standard Section Fields ──────────────────────── */
            section.fields && section.fields.length > 0 ? (
              <div className={`grid gap-4 w-full ${
                forceMobileLayout ? 'grid-cols-1 gap-3' : 'grid-cols-1 md:grid-cols-2 md:gap-6'
              }`}>
                {(() => {
                  const visibleFields = [...section.fields]
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .filter(field => shouldShowField(field) && !isFieldHiddenByRules(field.key));
                  return visibleFields.map((field, fieldIndex) => {
                    const required = effectiveFieldRequired(field);
                    const effectiveFieldNumber = fieldNumber != null ? fieldNumber + fieldIndex : undefined;
                    return (
                    <div
                      key={`${section.key}-${field.key}-${fieldIndex}`}
                      className={`${field.width || 'col-span-1'} ${
                        field.styling?.containerClasses || ''
                      }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DynamicField
                        field={{ ...field, required, is_required: required }}
                        value={formData[field.key]}
                        onChange={(value) => handleFieldChange(field.key, value)}
                        enableImageUpload={enableImageUpload}
                        fullFormData={fullFormData || formData}
                        readOnly={readOnly}
                        fieldNumber={effectiveFieldNumber}
                      />
                    </div>
                    );
                  });
                })()}
              </div>
            ) : (
              <div
                className={`text-center py-8 ${hasTheme ? '' : isDark ? 'text-gray-400' : 'text-gray-500'}`}
                style={hasTheme ? { color: themeConfig.answer_color } : undefined}
              >
                <p>No fields configured for this section.</p>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default DynamicSection;